import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FORMULATION_ITEM_FIELDS,
  FORMULATION_LABELS,
  FORMULATION_LIST_FIELDS,
  PENDING_KIND_LABEL,
} from '@/domain/workflow/formulation';
import { defaultTidyChoice, sourceText, type TidyPlan, type TidySource } from '@/domain/ai/formulationTidy';
import { AiBridge } from '@/services/aiBridge';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import { useRequiredSession } from '../../state/session';
import { ReceiveBox, SendButton } from '../../components/ExternalRoundTrip';
import { TextArea } from '../../components/fields';
import { flushAll } from '../../state/saves';

const CHANGE_KO = { keep: '그대로', edit: '문장 고침', merge: '합침', new: '새 항목' } as const;
const FIELD_ORDER = [...FORMULATION_ITEM_FIELDS, ...FORMULATION_LIST_FIELDS];

const sourceLabel = (s: TidySource) =>
  s.kind === 'opinion' ? `AI 의견(${PENDING_KIND_LABEL[s.opinion.kind]})` : FORMULATION_LABELS[s.field];

/**
 * 사례개념화 정리 (A38): 가설·의견이 많을 때 외부 AI에 합쳐 달라고 보내고, 답을 미리 보고 고른 것만 개정안에 넣는다.
 * 승인은 개정안에서 따로 한다.
 */
export function FormulationTidy({
  clientId,
  projectUrl,
  onApplied,
}: {
  clientId: string;
  projectUrl?: string | undefined;
  onApplied: () => void;
}) {
  const app = useRequiredSession();
  const svc = useService();
  const ai = useMemo(() => new AiBridge(app.repo, app.ctx, svc), [app, svc]);
  const [request, setRequest] = useState('');
  const req = useDeferredValue(request.trim());
  // 개정안을 고치면(자동 저장) 보낼 자료도 다시 만든다
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const on = (e: Event) => {
      if ((e as CustomEvent<{ entityType?: string }>).detail?.entityType === 'CaseFormulationRevision')
        setTick((t) => t + 1);
    };
    window.addEventListener('counselai:data-changed', on);
    return () => window.removeEventListener('counselai:data-changed', on);
  }, []);
  // 정리(회기 분석을 근거로, 빠름) / 다시 세우기(회기 전사까지 보내 처음부터, 정확)
  // 고르기 전에는 자동: 사례개념화가 비어 있으면 다시 세우기
  const [pickedMode, setRebuild] = useState<boolean | null>(null);
  const { data } = useLoad(
    () => ai.formulationTidyPacket(clientId, req || undefined, { rebuild: pickedMode ?? 'auto' }),
    [ai, clientId, req, tick, pickedMode],
  );
  const [preview, setPreview] = useState<{
    importId: string;
    plan: TidyPlan;
    warnings: string[];
    rebuild: boolean;
  } | null>(null);
  const [dropUntouched, setDropUntouched] = useState(false);
  const [rows, setRows] = useState<Set<number>>(new Set());
  const [drops, setDrops] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const hash = useLocation().hash;
  const [opened, setOpened] = useState(hash === '#tidy');
  // 검토 목록의 '많으면: AI로 합쳐 정리하기'에서 왔으면 이 칸으로
  useEffect(() => {
    if (hash === '#tidy' && data) document.getElementById('tidy')?.scrollIntoView({ block: 'start' });
  }, [hash, data]);
  if (!data) return null;
  const rebuild = data.rebuild;
  const total = data.counts.items + data.counts.lists;
  const many = total >= 12 || data.counts.opinions >= 3 || total === 0;
  const open = opened || many || !!preview;
  const toggle = (set: Set<number>, put: (s: Set<number>) => void, i: number) => {
    const n = new Set(set);
    if (n.has(i)) n.delete(i);
    else n.add(i);
    put(n);
  };

  const apply = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      await flushAll(); // 입력 중인 개정안을 먼저 저장
      const r = await ai.applyFormulationTidy(preview.importId, {
        rows: [...rows],
        drops: [...drops],
        dropUntouched: preview.rebuild && dropUntouched,
      });
      setDone(
        `정리를 개정안 v${r.revision.version}에 넣었어요 (합침 ${r.merged}, 버림 ${r.dropped}). 확인하고 승인하세요.`,
      );
      setPreview(null);
      onApplied();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card stack tidy-card" id="tidy">
      <div className="card-head">
        <h2>{data.counts.items + data.counts.lists ? 'AI로 정리하기' : 'AI로 사례개념화 세우기'}</h2>
        {!open && (
          <button type="button" onClick={() => setOpened(true)}>
            열기
          </button>
        )}
      </div>
      <p className="small muted">
        {total === 0 && !data.counts.opinions
          ? '아직 사례개념화가 비어 있어요. 아래에서 방식을 고르고 외부 AI에 보내면 칸을 처음부터 채워 와요. '
          : `지금 항목 ${total}개${data.counts.opinions ? ` · 연결 못한 AI 의견 ${data.counts.opinions}개` : ''}. `}
        답은 미리 보고 고른 것만 개정안에 넣어요. 승인은 따로 합니다.
      </p>
      {done && (
        <p className="ok small" role="status">
          {done}
        </p>
      )}
      {open && !preview && (
        <>
          {data.withTranscript > 0 && (
            <div className="tidy-mode" role="radiogroup" aria-label="정리 방식">
              <label className={rebuild ? 'mode-opt' : 'mode-opt on'}>
                <input type="radio" name="tidy-mode" checked={!rebuild} onChange={() => setRebuild(false)} />
                <span>
                  <strong>지금 항목 정리</strong>
                  <span className="muted small">
                    빠름. 회기마다 정리된 분석을 근거로 합치고, 맞는 칸으로 옮기고, 빈 칸을 채워요.
                  </span>
                </span>
              </label>
              <label className={rebuild ? 'mode-opt on' : 'mode-opt'}>
                <input type="radio" name="tidy-mode" checked={rebuild} onChange={() => setRebuild(true)} />
                <span>
                  <strong>전사까지 보내서 처음부터 다시 세우기</strong>
                  <span className="muted small">
                    정확. 회기 전사를 직접 읽고 모든 칸을 새로 세워요. 지금 항목은 전사로 확인되는 것만 살려요.
                    {rebuild && data.transcriptSessions.length
                      ? ` (${data.transcriptSessions.join('·')}회기 전사 포함, 자료가 길어요)`
                      : ''}
                  </span>
                </span>
              </label>
            </div>
          )}
          <TextArea
            label="요청 (선택)"
            value={request}
            onChange={setRequest}
            rows={2}
            placeholder="예: 신념 가설은 3개로, 가족 관련은 하나로 묶어줘"
          />
          <div className="row">
            <SendButton
              text={data.packet}
              projectUrl={projectUrl}
              label={rebuild ? '외부로 보내기 (전사로 다시 세우기)' : '외부로 보내기 (사례개념화 정리)'}
            />
          </div>
          <ReceiveBox
            title="정리 답 가져오기"
            hint="외부 AI의 답 전체를 복사해 오면 앱으로 돌아올 때 알아서 찾습니다."
            accept={(t) => /FORMULATION_TIDY_V1|"dropped"\s*:/.test(t)}
            onSubmit={async (raw) => {
              const r = await ai.importFormulationTidy(clientId, raw);
              if (!r.ok) throw new Error(`읽지 못했습니다: ${r.errors.join(', ')}`);
              const c = defaultTidyChoice(r.plan);
              setRows(new Set(c.rows));
              setDrops(new Set(c.drops));
              setDone(null);
              setDropUntouched(rebuild);
              setPreview({ importId: r.importId, plan: r.plan, warnings: r.warnings, rebuild });
            }}
          />
        </>
      )}
      {preview && (
        <TidyPreview
          plan={preview.plan}
          warnings={preview.warnings}
          rows={rows}
          drops={drops}
          rebuild={preview.rebuild}
          dropUntouched={dropUntouched}
          onDropUntouched={setDropUntouched}
          onRow={(i) => toggle(rows, setRows, i)}
          onDrop={(i) => toggle(drops, setDrops, i)}
          busy={busy}
          onApply={() => void apply()}
          onCancel={() => setPreview(null)}
        />
      )}
    </section>
  );
}

function TidyPreview({
  rebuild,
  dropUntouched,
  onDropUntouched,
  plan,
  warnings,
  rows,
  drops,
  onRow,
  onDrop,
  busy,
  onApply,
  onCancel,
}: {
  rebuild: boolean;
  dropUntouched: boolean;
  onDropUntouched: (v: boolean) => void;
  plan: TidyPlan;
  warnings: string[];
  rows: Set<number>;
  drops: Set<number>;
  onRow: (i: number) => void;
  onDrop: (i: number) => void;
  busy: boolean;
  onApply: () => void;
  onCancel: () => void;
}) {
  const changing = plan.rows.filter((r) => r.change !== 'keep');
  const kept = plan.rows.length - changing.length;
  const before = plan.before.items + plan.before.lists;
  const after = plan.after.items + plan.after.lists;
  const leftovers = rebuild && dropUntouched ? plan.untouched.length : 0;
  const picked =
    changing.filter((r) => rows.has(r.index)).length + plan.drops.filter((d) => drops.has(d.index)).length + leftovers;
  return (
    <div className="stack">
      <strong>
        항목 {before}개 → {after}개
        {plan.before.opinions ? ` · 의견 ${plan.before.opinions - plan.after.opinions}개 정리` : ''}
      </strong>
      <span className="small muted">모두 넣었을 때 기준이에요. 넣지 않을 줄은 체크를 끄세요.</span>
      {plan.stale && (
        <p className="warn small">
          보낸 뒤 개정안이 바뀌었어요. 번호가 맞지 않을 수 있으니 아래 원문을 꼭 확인하거나 다시 보내세요.
        </p>
      )}
      {[...warnings, ...(plan.unknownKeys.length ? [`없는 번호: ${plan.unknownKeys.join(', ')}`] : [])].map((w) => (
        <p key={w} className="muted small">
          {w}
        </p>
      ))}
      {plan.notes && <p className="small">AI 메모: {plan.notes}</p>}
      {FIELD_ORDER.map((f) => {
        const list = changing.filter((r) => r.field === f);
        if (!list.length) return null;
        return (
          <div key={f} className="stack tidy-group">
            <h3 className="small">{FORMULATION_LABELS[f]}</h3>
            {list.map((r) => (
              <label key={r.index} className="tidy-row">
                <input
                  type="checkbox"
                  checked={rows.has(r.index)}
                  onChange={() => onRow(r.index)}
                  aria-label={`${r.text} 넣기`}
                />
                <div className="stack">
                  <div>
                    <span className="status-pill">{CHANGE_KO[r.change]}</span> <strong>{r.text}</strong>
                  </div>
                  {r.why && <span className="muted small">{r.why}</span>}
                  {r.sources.length > 0 && (
                    <ul className="tidy-src small">
                      {r.sources.map((s) => (
                        <li key={s.key}>
                          <span className="badge">{sourceLabel(s)}</span> {sourceText(s)}
                        </li>
                      ))}
                    </ul>
                  )}
                  {r.hasConfirmed && (
                    <span className="warn small">확인됨 항목이 들어 있어 넣으면 '가설'로 바뀌어요.</span>
                  )}
                </div>
              </label>
            ))}
          </div>
        );
      })}
      {plan.drops.length > 0 && (
        <div className="stack tidy-group">
          <h3 className="small">버리기 제안</h3>
          {plan.drops.map((d) => (
            <label key={d.index} className="tidy-row">
              <input
                type="checkbox"
                checked={drops.has(d.index)}
                onChange={() => onDrop(d.index)}
                aria-label={`${sourceText(d.source)} 버리기`}
              />
              <div className="stack">
                <div>
                  <span className="badge">{sourceLabel(d.source)}</span> {sourceText(d.source)}
                </div>
                {d.why && <span className="muted small">{d.why}</span>}
              </div>
            </label>
          ))}
        </div>
      )}
      <span className="small muted">
        {kept ? `그대로 둔 항목 ${kept}개. ` : ''}
        {plan.untouched.length && !rebuild ? `AI가 말하지 않은 항목 ${plan.untouched.length}개도 그대로 둡니다.` : ''}
      </span>
      {rebuild && plan.untouched.length > 0 && (
        <div className="stack tidy-group">
          <label className="tidy-row">
            <input type="checkbox" checked={dropUntouched} onChange={(e) => onDropUntouched(e.target.checked)} />
            <div className="stack">
              <strong>AI가 말하지 않은 이전 항목 {plan.untouched.length}개도 빼기</strong>
              <span className="muted small">
                처음부터 다시 세우기라서, 전사로 다시 확인되지 않은 이전 항목은 빼는 게 맞아요. 남기려면 체크를 끄세요.
              </span>
              <ul className="tidy-src small">
                {plan.untouched.map((x) => (
                  <li key={x.key}>
                    <span className="badge">{sourceLabel(x)}</span> {sourceText(x)}
                  </li>
                ))}
              </ul>
            </div>
          </label>
        </div>
      )}
      <div className="row">
        <button type="button" className="primary" disabled={busy || !picked} onClick={onApply}>
          고른 것 개정안에 넣기 ({picked})
        </button>
        <button type="button" onClick={onCancel}>
          취소
        </button>
      </div>
    </div>
  );
}
